<template>
  <div class="wrapper">
    <dashboard-bar refreshButton />
    <div class="bg-white p-4">
      <div class="col-12 p-0 mb-3">
        <div class="header">
          <span class="header-icon">
            <i class="fas fa-clock"></i>
          </span>
          <h2>{{ $t('scheduling.pageTitle') }}</h2>
          <span
            class="ml-3 refresh-icon"
            title="Atualizar Lista"
            @click="getScheduling"
          >
            <font-awesome-icon :icon="['fas', 'sync-alt']" />
          </span>
        </div>
      </div>

      <div class="sub-header">
        <div class="filters">
          <div class="reminders-search-field">
            <input
              type="text"
              class="form-control"
              id="input-answer"
              :placeholder="$t('scheduling.searchInput')"
              v-model="filter"
              @keyup="searchByFilter"
            />
            <button @click="cleanFilter" class="btn btn-outline-primary">
              {{ $t('scheduling.searchButton') }}
            </button>
          </div>

          <b-form-select
            id="channel"
            v-model="selectedChannel"
            :options="optionChannel"
          ></b-form-select>
        </div>

        <div class="select-all-area">
          <div
            v-if="this.userProfile.permissions.submits.cancelScheduling"
            class="custom-switch"
          >
            <input
              type="checkbox"
              class="custom-control-input"
              id="switch-select-resend"
              v-model="pageSelected"
              :disabled="allSchedulingsSelected"
              @click="selectPage($event)"
            />
            <label class="custom-control-label" for="switch-select-resend">
              {{ $t('scheduling.selectPage') }}
            </label>
          </div>
        </div>
      </div>

      <transition name="fade">
        <div
          v-if="selectedIds.length > 0 || allSchedulingsSelected"
          class="select-all-area mb-3"
        >
          <div v-if="!allSchedulingsSelected" class="select-all-description">
            {{
              $t('scheduling.selectInfo.schedulingLength', {
                length: selectedIds.length,
              })
            }}

            <span @click="selectAllSchedulings" class="link idx-bold">
              {{
                $t('scheduling.selectInfo.selectAll', {
                  length: total,
                })
              }}
            </span>
          </div>

          <div v-else class="select-all-description">
            {{
              $t('scheduling.selectInfo.allSchedulings', {
                length: total,
              })
            }}

            <span @click="cancelAllSchedulings" class="link idx-bold">
              {{ $t('scheduling.selectInfo.cleanSelection') }}
            </span>
          </div>
        </div>
      </transition>

      <b-table striped :items="items" :fields="fields" :busy="loading">
        <template v-slot:table-busy>
          <div class="text-center text-primary my-2">
            <b-spinner type="grow" class="align-middle"></b-spinner>
          </div>
        </template>

        <template v-slot:cell(client)="row">
          <span class="user-info">
            {{ checkName(row.item.anonymousResponse, row.item.theName) }}
          </span>
          <br />
          <span class="user-info">
            E-mail:
            {{
              checkUserData(
                row.item.theEmail,
                userProfile.permissions.submits.anonymous_profile,
              )
            }}
          </span>
          <br />
          <span class="user-info">
            Telefone:
            {{
              checkUserData(
                row.item.thePhone,
                userProfile.permissions.submits.anonymous_profile,
              )
            }}
          </span>
        </template>

        <template
          v-if="this.userProfile.permissions.submits.cancelScheduling"
          v-slot:cell(cancel)="row"
        >
          <b-form-checkbox
            class="ml-3"
            :checked="verifyChecked(row.item._id) || allSchedulingsSelected"
            @change="cancelReminder(row.item._id, $event)"
            :disabled="allSchedulingsSelected"
          >
          </b-form-checkbox>
        </template>
      </b-table>

      <div class="footer">
        <div class="pagination-area-clients mt-1">
          <paginate
            v-model="page"
            :pageCount="Math.ceil(total / limit)"
            :prevText="'‹'"
            :nextText="'›'"
            :activeClass="'active'"
            :containerClass="'pagination b-pagination'"
            :pageClass="'page-item'"
          ></paginate>
        </div>

        <div class="buttons-area">
          <button type="button" @click="download('csv')" class="export-button">
            <i class="fas fa-file-csv" />
          </button>

          <button type="button" @click="download('xlsx')" class="export-button">
            <i class="fas fa-file-excel" />
          </button>

          <button
            v-if="this.userProfile.permissions.submits.cancelScheduling"
            type="button"
            class="btn btn-outline-primary cancel-button"
            @click="sendCancellation"
          >
            {{ $t('scheduling.cancelRemindersButton') }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Paginate from 'vuejs-paginate';
import { format } from 'date-fns';
import swal from 'sweetalert';
import dashboardBar from '@/components/molecules/dashboard-bar';
import api from '../../../services/api';

export default {
  components: { Paginate, dashboardBar },

  computed: {
    ...mapState('actions', ['myActionId', 'actionGroupData', 'myActionsIds']),
    ...mapState('branches', ['myIh1', 'myIh2', 'myHierarchies1', 'myHierarchies2']),
    ...mapState('authentication', ['userProfile']),
    ...mapState('calendar', ['calendarDates']),
    ...mapState('companies', ['myMetric']),
    ...mapState('indicators', ['indicatorName', 'indicatorData']),
  },

  data: () => ({
    selectedChannel: null,
    optionChannel: [],
    loading: false,
    userAuth: null,
    total: 0,
    page: 1,
    limit: 10,
    filter: null,
    fields: [],
    items: [],
    selectedIds: [],
    allSchedulingsSelected: false,
    pageSelected: false,
    numberOfChecked: 0,
  }),

  watch: {
    selectedChannel: function watchSelectedChannel() {
      this.getScheduling();
    },

    'actionGroupData._id': function watchGroupId() {
      this.getScheduling();
    },

    myActionId: function watchActionId() {
      this.getScheduling();
    },

    myActionsIds: function watchActionsIds() {
      this.getScheduling();
    },

    myIh1: function watchIh1() {
      this.getScheduling();
    },

    myHierarchies1: function watchMyHierarchies1() {
      this.getScheduling();
    },

    myIh2: function watchIh2() {
      this.getScheduling();
    },

    myHierarchies2: function watchMyHierarchies2() {
      this.getScheduling();
    },

    'calendarDates.end': function watchCalendarDates() {
      this.getScheduling();
    },

    myMetric: function watchMetric() {
      this.getScheduling();
    },

    indicatorData: function watchIndicatorData() {
      this.getScheduling();
    },

    page: function watchPage() {
      this.getScheduling();
    },
  },

  methods: {
    ...mapActions('calendar', ['setDefaultDates']),

    checkName(anonymous, name) {
      if (
        anonymous &&
        !this.userProfile.permissions.submits.anonymous_profile
      ) {
        return 'Anônimo';
      } else if (this.userProfile.permissions.submits.anonymous_profile) {
        return 'Nome: ***************';
      } else if (name) {
        return name;
      }
      return '-';
    },

    checkUserData(value, profile) {
      if (value === 'empty') {
        return '-';
      } else if (profile) {
        return '************';
      } else if (value) {
        return value;
      }
      return '-';
    },

    async getScheduling() {
      this.loading = true;

      this.pageSelected = false;

      const response = await api.get(`/scheduling`, {
        headers: {
          'x-access-token': this.userAuth.token,
        },
        params: {
          page: this.page,
          limit: this.limit,
          filter: this.filter,
          channel: this.selectedChannel,
          groupId: this.actionGroupData._id,
          actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
          startDate: this.calendarDates.start ? this.calendarDates.start : null,
          endDate: this.calendarDates.end ? this.calendarDates.end : null,
          ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
          ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
          metric: this.myMetric,
          indicator: this.indicatorName,
          indicatorValue: this.indicatorData.value,
          indicatorRange: this.indicatorData.range,
        },
      });

      this.items = response.data.details;
      this.total = response.data.total;

      this.items.forEach((el) => {
        el.submitId.date = this.dateFormat(el.submitId.date);
      });

      this.loading = false;
    },

    async sendCancellation() {
      if (this.selectedIds.length <= 0 && !this.allSchedulingsSelected) {
        swal({
          title: this.$t('scheduling.messages.cancelNoSelect.title'),
          text: this.$t('scheduling.messages.cancelNoSelect.description'),
          icon: 'warning',
        });
      } else {
        swal({
          title: this.$t('scheduling.messages.cancelQuestion.title'),
          text: this.$t('scheduling.messages.cancelQuestion.description'),
          icon: 'warning',
          buttons: true,
          dangerMode: true,
        }).then(async (answer) => {
          if (answer) {
            try {
              await api.delete(`/scheduling`, {
                data: {
                  all: this.allSchedulingsSelected,
                  ids: this.selectedIds.length > 0 ? this.selectedIds : null,
                },
                headers: {
                  'x-access-token': this.userAuth.token,
                },
                params: {
                  channel: this.selectedChannel,
                  groupId: this.actionGroupData._id,
                  actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
                  startDate: this.calendarDates.start
                    ? this.calendarDates.start
                    : null,
                  endDate: this.calendarDates.end
                    ? this.calendarDates.end
                    : null,
                  ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
                  ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
                  metric: this.myMetric,
                  indicator: this.indicatorName,
                  indicatorValue: this.indicatorData.value,
                  indicatorRange: this.indicatorData.range,
                },
              });

              this.selectedIds = [];
              this.allSchedulingsSelected = false;
              this.pageSelected = false;

              this.getScheduling();
            } catch (error) {
              swal(
                this.$t('scheduling.messages.cancelError.title'),
                'error',
                this.$t('scheduling.messages.cancelError.description'),
              );
            }
          }
        });
      }
    },

    async searchByFilter() {
      if (this.filter !== '' && this.filter !== null) {
        clearTimeout(this.typingTimer);
        this.typingTimer = setTimeout(this.getScheduling, 600);
      }

      if (this.filter === '') {
        this.filter = null;
        this.getScheduling();
      }
    },

    async download(extension) {
      // checks if the total docs to download is greater then 100k
      if (this.total > 100000) {
        swal({
          title: this.$t('scheduling.messages.maxDownloadError.title'),
          text: this.$t('scheduling.messages.maxDownloadError.description'),
          icon: 'warning',
        });
        return;
      }
      try {
        await api.get(`/scheduling/download/${extension}`, {
          headers: {
            'x-access-token': this.userAuth.token,
          },
          params: {
            filter: this.filter,
            channel: this.selectedChannel,
            groupId: this.actionGroupData._id,
            actionId: this.myActionsIds.length === 0 ? null : this.myActionsIds.join(','),
            startDate: this.calendarDates.start
              ? this.calendarDates.start
              : null,
            endDate: this.calendarDates.end ? this.calendarDates.end : null,
            ih1: this.myHierarchies1.length === 0 ? null : this.myHierarchies1.join(','),
            ih2: this.myHierarchies2.length === 0 ? null : this.myHierarchies2.join(','),
            metric: this.myMetric,
            indicator: this.indicatorName,
            indicatorValue: this.indicatorData.value,
            indicatorRange: this.indicatorData.range,
          },
        });

        swal({
          title: this.$t('scheduling.messages.download.title'),
          text: this.$t('scheduling.messages.download.description'),
          icon: 'success',
        });
      } catch (error) {
        swal({
          title: this.$t('scheduling.messages.downloadError.title'),
          text: error.response.data.message,
          icon: 'error',
        });
      }
    },

    cleanFilter() {
      if (this.filter) {
        this.filter = null;

        this.getScheduling();
      }
    },

    dateFormat(date) {
      if (date) {
        return format(new Date(date), 'dd/MM/yyyy - HH:mm');
      }

      return '-';
    },

    cancelReminder(reminderId, checked) {
      if (checked) {
        this.selectedIds.push(reminderId);
      } else {
        this.selectedIds = this.selectedIds.filter((id) => id !== reminderId);
      }
    },

    selectAllSchedulings() {
      this.allSchedulingsSelected = true;

      this.pageSelected = true;

      this.selectedIds = [];
    },

    cancelAllSchedulings() {
      this.allSchedulingsSelected = false;

      this.pageSelected = false;
    },

    verifyChecked(scheduleId) {
      const idFound = this.selectedIds.indexOf(scheduleId);

      this.verifyPageSelected();

      if (idFound >= 0) {
        return true;
      }

      return false;
    },

    selectPage(event) {
      if (event.target.checked) {
        this.items.forEach((schedule) => {
          const idFound = this.selectedIds.indexOf(schedule._id);

          if (idFound < 0) {
            this.selectedIds.push(schedule._id);
          }
        });
      } else {
        this.items.forEach((schedule) => {
          const idFound = this.selectedIds.indexOf(schedule._id);

          if (idFound >= 0) {
            this.selectedIds.splice(idFound, 1);
          }
        });
      }
    },

    verifyPageSelected() {
      if (this.allSchedulingsSelected) {
        this.pageSelected = true;
      } else {
        const ids = this.items.filter((schedule) =>
          this.selectedIds.includes(schedule._id),
        );

        if (ids.length >= this.items.length) {
          this.pageSelected = true;
        } else {
          this.pageSelected = false;
        }
      }
    },
  },

  created() {
    this.userAuth = JSON.parse(localStorage.getItem('_indecx'));

    this.fields = [
      {
        key: 'controlId',
        label: this.$t('scheduling.schedulingTable.labels.id'),
      },
      {
        key: 'actionId.name',
        label: this.$t('scheduling.schedulingTable.labels.action'),
      },
      {
        key: 'client',
        label: this.$t('scheduling.schedulingTable.labels.client'),
      },
      {
        key: 'submitId.date',
        label: this.$t('scheduling.schedulingTable.labels.date'),
      },
      {
        key: 'actionId.channel',
        label: this.$t('scheduling.schedulingTable.labels.channel'),
      },
      {
        key: 'cancel',
        class: 'text-center',
        label: this.$t('scheduling.schedulingTable.labels.cancel'),
      },
    ];

    this.optionChannel = [
      { value: null, text: this.$t('scheduling.channelFilter.select') },
      { value: 'email', text: this.$t('scheduling.channelFilter.email') },
      { value: 'sms', text: this.$t('scheduling.channelFilter.sms') },
      { value: 'emailsms', text: this.$t('scheduling.channelFilter.all') },
    ];

    // this.setDefaultDates();

    this.getScheduling();
  },
};
</script>

<style lang="scss" scoped>
@import '@/themes/default/variables.scss';
@import '@/themes/default/placeholders.scss';

.wrapper {
  padding: 8px;
}

h2 {
  text-transform: uppercase;
  font-size: 21px;
  font-weight: 100;
  color: $gray;
}

.header {
  display: flex;
  flex-direction: row;

  .header-icon {
    float: left;
    font-size: 12px;
    margin-top: 4px;
    padding-right: 12px;
    color: $gray;
  }

  .refresh-icon {
    margin-top: 1px;
    color: $primary;
    cursor: pointer;
    &:hover {
      color: green;
    }
  }
}

.sub-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 24px;

  .filters {
    display: flex;
    flex-direction: row;

    .reminders-search-field {
      display: flex;
      flex-direction: row;
      min-width: 300px;
      margin-right: 24px;

      input {
        border-bottom-right-radius: 0;
        border-top-right-radius: 0;
      }

      button {
        border-bottom-left-radius: 0;
        border-top-left-radius: 0;
      }
    }
  }
}

.select-all-area {
  display: flex;
  justify-content: center;
  align-items: center;

  .select-all-description {
    padding: 10px 30px;
    color: #5f6368;
    background-color: rgba(241, 243, 244, 0.6);
  }

  .link {
    color: #1a73e8;
    cursor: pointer;
  }
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.user-info {
  color: rgb(133, 131, 131);
}

.pagination-area-clients >>> a {
  position: relative;
  display: block;
  padding: 0.5rem 0.75rem;
  margin-left: -1px;
  line-height: 1.25;
  color: #4ce595;
  background-color: #fff;
  border: 1px solid #dee2e6;
}

.footer {
  display: flex;
  justify-content: space-between;

  .cancel-button {
    max-height: 40px;
    margin-bottom: 16px;
  }

  .export-button {
    color: $primary;
    font-size: 34px;
    border: none;
    background: none;
    margin: 0;
    padding: 0;
    margin-right: 8px;
    cursor: pointer;
  }
}
</style>

<style>
.pagination-area-clients .active a {
  text-decoration: none;
  z-index: 1;
  color: #fff;
  background-color: #4ce595;
  border-color: #4ce595;
}

.pagination-area-clients a {
  outline: none;
}
</style>



// WEBPACK FOOTER //
// src/components/organisms/scheduling/index.vue